import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Chip,
  Slider,
  CircularProgress,
  IconButton,
  Alert,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import SendIcon from "@mui/icons-material/Send";
import API from "../../utils/Axios.utils";

const InterviewEvaluationModal = ({
  open,
  onClose,
  candidate,
  onSuccess,
}) => {
  const isAlreadySubmitted = Boolean(candidate?.feedback_submitted || candidate?.interview_feedback);
  const initialDecision = candidate?.evaluation_decision || (candidate?.detailed_status === 'Rejected' ? 'failed' : 'passed');
  const [decision, setDecision] = useState(initialDecision);
  const [score, setScore] = useState(candidate?.score || (initialDecision === "passed" ? 85 : 40));
  const [feedback, setFeedback] = useState(candidate?.interview_feedback || candidate?.feedback || "");
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (candidate) {
      const dec = candidate?.evaluation_decision || (candidate?.detailed_status === 'Rejected' ? 'failed' : 'passed');
      setDecision(dec);
      setScore(candidate?.score || (dec === "passed" ? 85 : 40));
      setFeedback(candidate?.interview_feedback || candidate?.feedback || "");
    }
  }, [candidate]);

  if (!candidate) return null;

  const candidateName = candidate.candidate_name || "Candidate";
  const stage = candidate.stage || candidate.detailed_status || "L3_Round";
  const jobCode = candidate.job_code || candidate.job_title || "";

  const handleSubmit = async () => {
    setSubmitting(true);
    setErrorMsg("");
    try {
      const payload = {
        decision: decision, // "passed" or "failed"
        score: Number(score),
        feedback: feedback.trim(),
      };

      const res = await API.post(
        `api/interview/${candidate.profile_id}/l3-evaluation`,
        payload
      );

      if (onSuccess) {
        onSuccess(res.data?.data || payload);
      }
      onClose();
    } catch (err) {
      console.error("Evaluation submission failed:", err);
      setErrorMsg(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to submit evaluation. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === "backdropClick") return;
        if (!submitting) onClose();
      }}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
        },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 2.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid #E2E8F0",
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700} color="#1E293B">
            Submit Interview Feedback
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Evaluate candidate outcome &amp; trigger pipeline automation
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          disabled={submitting}
          sx={{ color: (theme) => theme.palette.grey[500] }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Candidate Info Summary Bar */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 1.5,
            mb: 2.5,
            bgcolor: "#F8FAFC",
            borderRadius: 2,
            border: "1px solid #E2E8F0",
          }}
        >
          <Box>
            <Typography variant="subtitle2" fontWeight={600} color="#0F172A">
              {candidateName}
            </Typography>
            {jobCode && (
              <Typography variant="caption" color="text.secondary">
                Job: {jobCode}
              </Typography>
            )}
          </Box>
          <Chip
            label={stage === "L3_Round" ? "L3 Round" : stage}
            color="primary"
            size="small"
            variant="outlined"
            sx={{ fontWeight: 600, fontSize: "0.75rem" }}
          />
        </Box>

        {isAlreadySubmitted && (
          <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
            <strong>Feedback Recorded:</strong> This candidate has already been evaluated ({decision === "passed" ? "Passed" : "Failed"}). You can review or update the evaluation below.
          </Alert>
        )}

        {errorMsg && (
          <Alert severity="error" sx={{ mb: 2.5 }}>
            {errorMsg}
          </Alert>
        )}

        {/* Decision Toggle Cards */}
        <Typography variant="subtitle2" fontWeight={600} mb={1} color="#334155">
          Interview Outcome <span style={{ color: "red" }}>*</span>
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mb: 3 }}>
          <Paper
            elevation={0}
            onClick={() => setDecision("passed")}
            sx={{
              p: 2,
              borderRadius: 2,
              cursor: "pointer",
              textAlign: "center",
              border: "2px solid",
              borderColor: decision === "passed" ? "#10B981" : "#E2E8F0",
              bgcolor: decision === "passed" ? "#ECFDF5" : "#FFFFFF",
              transition: "all 0.2s ease-in-out",
              "&:hover": { borderColor: "#10B981" },
            }}
          >
            <CheckCircleOutlineIcon
              sx={{
                fontSize: 32,
                color: decision === "passed" ? "#059669" : "#94A3B8",
                mb: 0.5,
              }}
            />
            <Typography
              variant="subtitle2"
              fontWeight={700}
              color={decision === "passed" ? "#065F46" : "#475569"}
            >
              Pass (Clear Round)
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Advances to next stage
            </Typography>
          </Paper>

          <Paper
            elevation={0}
            onClick={() => setDecision("failed")}
            sx={{
              p: 2,
              borderRadius: 2,
              cursor: "pointer",
              textAlign: "center",
              border: "2px solid",
              borderColor: decision === "failed" ? "#EF4444" : "#E2E8F0",
              bgcolor: decision === "failed" ? "#FEF2F2" : "#FFFFFF",
              transition: "all 0.2s ease-in-out",
              "&:hover": { borderColor: "#EF4444" },
            }}
          >
            <HighlightOffIcon
              sx={{
                fontSize: 32,
                color: decision === "failed" ? "#DC2626" : "#94A3B8",
                mb: 0.5,
              }}
            />
            <Typography
              variant="subtitle2"
              fontWeight={700}
              color={decision === "failed" ? "#991B1B" : "#475569"}
            >
              Fail (Reject)
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Moves to Rejected stage
            </Typography>
          </Paper>
        </Box>

        {/* Evaluation Score */}
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography variant="subtitle2" fontWeight={600} color="#334155">
              Evaluation Score (0 - 100)
            </Typography>
            <Chip
              label={`${score} / 100`}
              size="small"
              sx={{
                fontWeight: 700,
                bgcolor:
                  decision === "passed"
                    ? score >= 75
                      ? "#DCFCE7"
                      : "#FEF3C7"
                    : "#FEE2E2",
                color:
                  decision === "passed"
                    ? score >= 75
                      ? "#166534"
                      : "#92400E"
                    : "#991B1B",
              }}
            />
          </Box>
          <Slider
            value={score}
            min={0}
            max={100}
            step={1}
            onChange={(_, val) => setScore(val)}
            valueLabelDisplay="auto"
            disabled={submitting}
            sx={{
              color: decision === "passed" ? "#10B981" : "#EF4444",
              "& .MuiSlider-thumb": {
                width: 20,
                height: 20,
                "&:hover, &.Mui-focusVisible": {
                  boxShadow: "0 0 0 8px rgba(16, 185, 129, 0.16)",
                },
              },
            }}
          />
        </Box>

        {/* Detailed Feedback Field */}
        <Typography variant="subtitle2" fontWeight={600} mb={1} color="#334155">
          Detailed Feedback &amp; Notes
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={4}
          placeholder="Enter technical observations, strengths, weaknesses, and rationale for your decision..."
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          disabled={submitting}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              bgcolor: "#F8FAFC",
            },
          }}
        />

        {/* Pipeline Automation Notice */}
        <Alert severity="info" sx={{ mt: 2.5, borderRadius: 2 }}>
          Submitting this evaluation will automatically notify the Recruitment Flow webhook{" "}
          (<code>/webhook/l3-result</code>) and update the candidate's stage.
        </Alert>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, borderTop: "1px solid #E2E8F0" }}>
        <Button
          onClick={onClose}
          disabled={submitting}
          sx={{ textTransform: "none", color: "#64748B", fontWeight: 600 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            px: 3,
            bgcolor: decision === "passed" ? "#2777AA" : "#DC2626",
            "&:hover": {
              bgcolor: decision === "passed" ? "#1E5E87" : "#B91C1C",
            },
          }}
        >
          {submitting ? "Saving..." : isAlreadySubmitted ? "Update Evaluation" : "Submit Evaluation"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InterviewEvaluationModal;
